import React, {useCallback, useEffect} from 'react';
import {Button, Steps, Flex, Typography} from 'antd';
import {useStepsProps} from "./hooks/useStepsProps";
import {ChooseLoginMethodStep} from "./steps/ChooseLoginMethodStep";
import {LoginViewStore, resetLoginViewStore} from "./view-store";
import {Page} from "./ui/Page";
import {Content} from "./ui/Content";
import {ButtonsWrapper} from "./ui/ButtonsWrapper";
import {Window} from "./ui/Window";
import {CentredTitle} from "./ui/CentredTitle";
import {AuthorizationStep} from "./steps/AuthorizationStep";
import {Nullary} from "./ui/Nullary";
import {InputMemberStep} from "./steps/InputMemberStep";

import {useTranslation} from "react-i18next";
import {toast, ToastContainer} from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import styled from "@emotion/styled";
import {CloseButton} from "./ui/CloseButton";
import {UserNotFoundError} from "./errors/UserNotFoundError";
import {UserEmailNotFoundError} from "./errors/UserEmailNotFoundError";
import {AppStore} from "../../useAppPullState";
import {Api} from "./api/api";
import {firebaseService} from "../../service/firebase";
import {SelectMemberModal} from "./modals/SelectMemberModal";

const content = [
    InputMemberStep,
    ChooseLoginMethodStep,
    AuthorizationStep
]

const StyledSteps = styled(Steps)`
  padding-top: 12px;
  padding-bottom: 36px;
`

const Error = styled(Typography.Text)`
    color: #a8071a;
    margin-top: 12px;
    text-align: center;
`


const closeLogin = () => {
    AppStore.update(s => {
        s.loginPage = false;
    });
}


const setLoading = (loading) => {
    LoginViewStore.update(s => {
        s.loading = loading;
    });
}


export const loginStatusesTranslations = {
    "en": {
        "checking": "Checking credentials...",
        "invalid-password": "Invalid password",
        "login-success": "Logged in successfully"
    },
    "fi": {
        "checking": "Tarkistetaan kirjautumistunnuksia...",
        "invalid-password": "Väärä salasana",
        "login-success": "Kirjautuminen onnistui"
    }
}


export const LoginView = () => {
    const steps = useStepsProps();
    const {t} = useTranslation(['login']);
    const {t: tStatus} = useTranslation(['loginStatuses']);
    const loading = LoginViewStore.useState(s => s.loading);
    const StepComponent = content[steps.current] || Nullary;
    const magicLink = LoginViewStore.useState(s => s.magicLink);
    const error = LoginViewStore.useState(s => s.error);
    const nextDisabled = LoginViewStore.useState(s => s.nextDisabled);

    const loginPage = AppStore.useState(s => s.loginPage);
    useEffect(() => {
        if (loginPage) {
            resetLoginViewStore();
        }
    }, [loginPage]);


    const {email , password, member} = LoginViewStore.useState(s => {
        return {
            email: s.email,
            password: s.password,
            member: s.member
        }
    });
    const login = useCallback(async (memberId) => {
            setLoading(true);
            const loadingToastId = toast.loading(tStatus('checking'));

            await Api.login({email, password, member: member || memberId}).then(({error, token, users}) => {

                if (error) {
                    toast.error(tStatus(`invalid-password`));
                }else if(token) {
                    firebaseService.viaToken(token);
                    toast.success(tStatus(`login-success`));
                }else if(users){
                    LoginViewStore.update(s => {
                        s.users = users;
                        s.selectUsers = true;
                        s.selectUsersAction = login;
                    });
                }
            });
            setLoading(false);
            toast.dismiss(loadingToastId);
    }, [email, member, password]);
    return (
        <Page>

            <Window>
                <Flex>

                <StyledSteps
                    {...steps}
                />
                    <CloseButton onClick={closeLogin}/>
                </Flex>
                <Content>
                    <CentredTitle level={3}>{t('authorization')}</CentredTitle>
                    <StepComponent/>
                    {error === "user-not-found" ? <UserNotFoundError/> : null}
                    {error === "email-not-found" ? <UserEmailNotFoundError/> : null}
                    <ButtonsWrapper>
                        {steps.current > 0 && (
                            <Button style={{margin: '0 8px'}} onClick={() => steps.prev()}>
                                {t('previous')}
                            </Button>
                        )}
                        {steps.current < steps.items.length - 1 && steps.current !== 1 && (
                            <Button
                                loading={loading}
                                type="primary"
                                onClick={() => steps.next()}
                                disabled={nextDisabled}
                            >
                                {t('next')}
                            </Button>
                        )}
                        {steps.current === steps.items.length - 1 && !magicLink && (
                            <Button type="primary" onClick={() => login()}>
                                {t('submit')}
                            </Button>
                        )}
                    </ButtonsWrapper>
                </Content>


            </Window>
            <SelectMemberModal/>
            <ToastContainer position='bottom-right'/>
        </Page>
    );
}
